import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";

import { Button, FloatingActionBar } from "@chef/components";
import {
  BRAND_PATHS,
  FILTER_CATEGORIES,
  PRODUCT_TYPE_IDS,
} from "@chef/constants";
import { getCutoffDateForWeek } from "@chef/helpers";
import { usePickAndMixQuery } from "@chef/state-management";
import {
  filterPickAndMix,
  generateFilterCategoriesFromRecipes,
} from "@chef/state-management/helpers";
import { AppShell } from "@chef/feature-app-shell";
import { useIntercom } from "@chef/hooks";
import {
  ErrorComponent,
  RecipeActionModal,
  FilterNavigation,
} from "@chef/smart-components";
import { isEqualStrings } from "@chef/utils/equal";
import { useFilters } from "@chef/state-management/hooks";

import { PickAndMixCardGrid } from "../lib/PickAndMixCardGrid";
import { usePickAndMixProductWithPrice } from "../hooks";

import { intl } from "./OnTheMenu.Intl";

interface OnTheMenuProps {
  week: number;
  year: number;
}

export const OnTheMenu = ({ week, year }: OnTheMenuProps) => {
  return <OnTheMenuMainComponent key={week} week={week} year={year} />;
};

const OnTheMenuMainComponent = ({ week, year }: OnTheMenuProps) => {
  useIntercom(false);

  const router = useRouter();

  const { updateUrl } = useFilters();

  const [currentFilter, setCurrentFilter] = useState(
    FILTER_CATEGORIES.ALL_CATEGORIES_SLUG as string,
  );

  const {
    data: pickAndMixData,
    isError: isErrorPickAndMix,
    isLoading: isLoadingPickAndMix,
  } = usePickAndMixQuery({
    productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
    week,
    year,
  });

  const filteredPickAndMix = useMemo(() => {
    return filterPickAndMix({
      pickAndMix: pickAndMixData?.pickAndMix || [],
      cutoffDate: getCutoffDateForWeek({ week, year }),
    });
  }, [pickAndMixData?.pickAndMix, week, year]);

  const { isLoadingExtendedBasketData, isErrorExtendedBasketData, pickAndMix } =
    usePickAndMixProductWithPrice({
      week,
      year,
      sortedPickAndMix: filteredPickAndMix,
      canSelectProducts: false,
    });

  const isLoading = isLoadingPickAndMix || isLoadingExtendedBasketData;
  const isError = isErrorPickAndMix || isErrorExtendedBasketData;

  useEffect(() => {
    if (router.query["filter"]) {
      setCurrentFilter(router.query["filter"] as string);
    }
  }, [router.query]);

  const filters = useMemo(() => {
    return generateFilterCategoriesFromRecipes({
      recipes: pickAndMix.flatMap((pnm) => pnm.recipes),
      currentFilter,
      recommendations: null,
      favorites: null,
    });
  }, [pickAndMix, currentFilter]);

  const filterInformation = filters.find((f) =>
    isEqualStrings(f.value, currentFilter),
  );

  if (isError) {
    return <ErrorComponent />;
  }

  if (isLoading) {
    return null;
  }

  return (
    <>
      <AppShell.Main grid>
        <div className="flex flex-col items-center justify-center col-span-full">
          <h1 className="text-2xl lg:text-4xl">
            <strong>{intl.ON_MENU_TITLE}</strong>
          </h1>
          <p className="text-center md:mt-2">{intl.ON_MENU_DESCRIPTION}</p>
        </div>
        <div className="-mx-4 text-center col-span-full">
          <FilterNavigation
            items={filters}
            value={currentFilter}
            onChange={(val) => updateUrl(val)}
            resetFilters={() =>
              updateUrl(FILTER_CATEGORIES.ALL_CATEGORIES_SLUG)
            }
            leftClassName="bg-gradient-to-r from-background via-background"
            rightClassName="bg-gradient-to-l from-background via-background"
            className="px-4"
          />
        </div>

        <PickAndMixCardGrid
          items={filteredPickAndMix}
          filter={filterInformation}
          portions={4}
          week={week}
          year={year}
          resetFilters={() => {
            updateUrl(FILTER_CATEGORIES.ALL_CATEGORIES_SLUG);
          }}
          className="col-span-full"
          hideFooter
          signup
        />
      </AppShell.Main>

      <RecipeActionModal />

      <FloatingActionBar className="sticky">
        <Link href={BRAND_PATHS.SIGNUP_HREF} passHref legacyBehavior>
          <Button
            className="col-span-2 md:col-span-3 md:col-start-6 xl:col-start-10"
            primary
          >
            {intl.ORDER_HERE}
          </Button>
        </Link>
      </FloatingActionBar>
    </>
  );
};
